import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FilterableTableHeader } from "../components/ui/filterable-table-header";
import { IPWhitelistModal } from "../components/staff/IPWhitelistModal";
import { FastLoginModal } from "../components/staff/FastLoginModal";
import { Plus, Search, Shield, Link as LinkIcon, Edit, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { deleteUser, fetchAvailableUsers, fetchUsers } from "../services/crmServices/userService";
import Swal from "sweetalert2";
import { useAuth } from "../hooks/useAuth";

export function Staff() {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [staff, setStaff] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showIPModal, setShowIPModal] = useState(false);
    const [showFastLoginModal, setShowFastLoginModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [positionFilter, setPositionFilter] = useState([]);

    // Get unique positions for filter
    const uniquePositions = [...new Set(staff.map((member) => member.position.name))].sort();

    const filteredStaff = staff.filter((member) => {
        const matchesSearch =
            member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member._id.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesPosition = positionFilter.length === 0 || positionFilter.includes(member.position.name);

        return matchesSearch && matchesPosition;
    });

    const totalPages = Math.ceil(filteredStaff.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedStaff = filteredStaff.slice(startIndex, startIndex + pageSize);

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handleAddStaff = () => {
        navigate("/staff/create");
    };

    const handleEditStaff = (staffMember) => {
        navigate(`/staff/edit/${staffMember._id}`);
    };

    const handleIPWhitelist = () => {
        setShowIPModal(true);
    };

    const handleFastLogin = (staffMember) => {
        if (staffMember.canFastLogin) {
            setSelectedStaff(staffMember);
            setShowFastLoginModal(true);
        } else {
            toast.error("Fast login is disabled for this staff member. Enable it in user settings first.");
        }
    };

    const getViewLeadsColor = (viewLeads) => {
        switch (viewLeads) {
            case "All":
                return "bg-green-100 text-green-800";
            case "Desk":
                return "bg-blue-100 text-blue-800";
            case "Floor":
                return "bg-purple-100 text-purple-800";
            case "Assigned":
                return "bg-orange-100 text-orange-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }

        initPage();
    }, []);

    const initPage = () => {
        setLoading(true);
        fetchAvailableUsers()
            .then((res) => {
                if (res?.data?.length) {
                    // setStaff(res.data.filter((member) => member._id !== user._id));
                    setStaff(res.data);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = (_id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this staff member!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, keep it"
        }).then((result) => {
            if (result.isConfirmed) {
                deleteUser({ _id }).then((res) => {
                    if (res.status !== 200) {
                        toast.error("Something went wrong while deleting the staff member.");
                        return;
                    } else {
                        toast.success("Staff member deleted successfully");
                        initPage();
                    }
                });
            }
        });
    };

    const handleBack = () => {
        navigate("/");
    };

    const handleFilterChange = (filterKey, values) => {
        if (filterKey === "position") {
            setPositionFilter(values);
            setCurrentPage(1);
        }
    };

    if (loading) {
        return (
            <div className="w-full max-w-[1600px] mx-auto px-4">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[1600px] mx-auto px-4">
            <div className="mb-4">
                <div className="mb-3">
                    <h1 className="text-xl font-semibold text-gray-900">Staff Management</h1>
                    <p className="text-xs text-gray-600 mt-0.5">Manage team members and permissions</p>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <Input
                            placeholder="Search staff..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 h-8 text-sm"
                        />
                    </div>

                    {user.position === "Admin" && (
                        <button
                            onClick={handleIPWhitelist}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                        >
                            <Shield className="h-3.5 w-3.5 mr-1.5" />
                            IP Whitelist
                        </button>
                    )}

                    {user.permissions.staff.create && (
                        <button
                            onClick={handleAddStaff}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                            Add Staff
                        </button>
                    )}
                </div>
            </div>

            <div className="rounded border border-gray-200 overflow-x-auto bg-white">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="text-xs py-2">Actions</TableHead>
                            <TableHead className="text-xs py-2 min-w-32">Name</TableHead>
                            <TableHead className="text-xs py-2">Username</TableHead>
                            <TableHead className="text-xs py-2 min-w-48">Email</TableHead>
                            {user.position.role === "admin" && <TableHead className="text-xs py-2">Fast Login</TableHead>}
                            <FilterableTableHeader
                                filterKey="position"
                                options={uniquePositions}
                                selectedValues={positionFilter}
                                onFilterChange={handleFilterChange}
                                placeholder="Filter..."
                                searchPlaceholder="Search..."
                                className="text-xs py-2"
                            >
                                Position
                            </FilterableTableHeader>
                            <TableHead className="text-xs py-2">Desk</TableHead>
                            <TableHead className="text-xs py-2">Leads</TableHead>
                            <TableHead className="text-xs py-2">View Scope</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="text-xs">
                        {paginatedStaff.map((member) => (
                            <TableRow key={member._id}>
                                <TableCell className="py-2">
                                    <div className="flex gap-1">
                                        {user.permissions.staff.update && (
                                            <button
                                                onClick={() => handleEditStaff(member)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded transition-colors"
                                            >
                                                <Edit className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                        {user.permissions.staff.delete && (
                                            <button
                                                onClick={() => handleDelete(member._id)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-red-600 bg-white border border-red-300 hover:bg-red-50:bg-red-900/30 rounded transition-colors"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="font-medium py-2 min-w-32">
                                    {member.firstName + (member.lastName ? " " + member.lastName : "")}
                                </TableCell>
                                <TableCell className="py-2">{member.userName || "-"}</TableCell>
                                <TableCell className="py-2 min-w-48">
                                    <div className="truncate">{member.email}</div>
                                </TableCell>
                                {user.position.role === "admin" && (
                                    <TableCell className="py-2">
                                        {member.canFastLogin ? (
                                            <button
                                                onClick={() => handleFastLogin(member)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-blue-600 bg-white border border-blue-300 hover:bg-blue-50:bg-blue-900/30 rounded transition-colors"
                                            >
                                                <LinkIcon className="h-3.5 w-3.5" />
                                            </button>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </TableCell>
                                )}

                                <TableCell className="py-2">
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">{member.position.name}</Badge>
                                </TableCell>
                                <TableCell className="py-2">
                                    {member.position.role === "admin" && "-"}
                                    {member.position.role === "floorManager" && (
                                        <div className="flex flex-wrap gap-1">
                                            {member.floorDesks?.map((desk, index) => (
                                                <Badge key={index} variant="secondary" className="text-[10px] px-1.5 py-0.5">
                                                    {desk.name}
                                                </Badge>
                                            )) || "-"}
                                        </div>
                                    )}
                                    {member.position.role === "deskManager" && (
                                        <div className="flex flex-wrap gap-1">
                                            {member.desks?.map((desk, index) => (
                                                <Badge key={index} variant="secondary" className="text-[10px] px-1.5 py-0.5">
                                                    {desk.name}
                                                </Badge>
                                            )) || "-"}
                                        </div>
                                    )}
                                    {member.position.role === "agent" && member.agentDesk && (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                                            {member.agentDesk.name}
                                        </Badge>
                                    )}
                                    {member.position.role === "agent" && !member.agentDesk && "-"}
                                </TableCell>
                                <TableCell className="py-2">
                                    {member.position.role !== "agent" ? (
                                        "-"
                                    ) : (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                                            {member.leads?.length || 0}
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="py-2">
                                    <Badge variant="secondary" className={`${getViewLeadsColor(member.viewLeadsScope ?? "Assigned")} text-[10px] px-1.5 py-0.5`}>
                                        {member.viewLeadsScope ?? "Assigned"}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Compact Pagination */}
            <div className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded mt-3">
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-600">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white text-gray-900"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-xs text-gray-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>

            {filteredStaff.length === 0 && (
                <div className="text-center py-8 bg-white border border-gray-200 rounded mt-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">No staff members found</h3>
                    <p className="text-xs text-gray-600">Try adjusting your search criteria</p>
                </div>
            )}

            <IPWhitelistModal open={showIPModal} onClose={() => setShowIPModal(false)} />


            <FastLoginModal
                open={showFastLoginModal}
                onClose={() => {
                    setShowFastLoginModal(false);
                    setSelectedStaff(null);
                }}
                staff={selectedStaff}
            />
        </div>
    );
}
